import { type Ref, shallowRef, watch } from "vue";
import { useScroll, useThrottleFn } from "@vueuse/core";
import { findScrollableParents } from "../utils/dom";

interface UseScrollHandlersOptions {
  activatorRef: Ref<HTMLElement | undefined>;
  onScroll: () => void;
}

export const useScrollHandlers = ({ activatorRef, onScroll }: UseScrollHandlersOptions) => {
  const scrollableParents = shallowRef<Element[]>([]);
  const scrollStopHandlers = shallowRef<(() => void)[]>([]);
  let windowScrollStop: (() => void) | null = null;

  const throttledOnScroll = useThrottleFn(onScroll, 16, true);

  const setupWindowScroll = () => {
    if (!windowScrollStop) {
      const { x, y } = useScroll(window);
      windowScrollStop = watch([x, y], throttledOnScroll);
    }
  };

  const cleanupWindowScroll = () => {
    if (windowScrollStop) {
      windowScrollStop();
      windowScrollStop = null;
    }
  };

  const refreshScrollableParents = () => {
    if (!activatorRef.value) return;

    // Clean up existing handlers
    scrollStopHandlers.value.forEach(stop => stop());
    scrollStopHandlers.value = [];

    // Find and watch scrollable parents
    scrollableParents.value = findScrollableParents(activatorRef.value);

    scrollableParents.value.forEach(parent => {
      const { x, y } = useScroll(parent as HTMLElement);
      const stopWatcher = watch([x, y], throttledOnScroll);
      scrollStopHandlers.value.push(stopWatcher);
    });
  };

  const setupScrollHandlers = () => {
    setupWindowScroll();
    refreshScrollableParents();
  };

  const cleanupScrollHandlers = () => {
    scrollStopHandlers.value.forEach(stop => stop());
    scrollStopHandlers.value = [];
    cleanupWindowScroll();
  };

  return {
    setupScrollHandlers,
    cleanupScrollHandlers,
  };
};
